import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Form } from 'src/common';
import styles from './index.module.scss';
import classNames from 'classnames';
import { LearnMoreLink } from 'src/shared';
import { useForm } from 'antd/lib/form/Form';
import TenderFilesTree from './TenderFilesTree';
import { useGenerateBidGroups } from 'src/models/bids/BidFull/hooks';
import { hitGroupLimits } from 'src/models/bids/BidFull/helpers';
import { Bidding } from 'src/models/bids/BidFull';
import TenderNavData from 'src/models/procurements/TenderNav';
import { isFieldString } from 'src/models/procurements/Tender/types';
import { trackBTGenerateTask } from 'src/segment/events';

interface IPrePopulateModalProps {
  biddingData: Bidding | null;
  tenderNavData: TenderNavData | null;
  onCloseModal: () => void;
}

export const PrePopulateModal: FC<IPrePopulateModalProps> = ({ biddingData, tenderNavData, onCloseModal }) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const bidId = biddingData?.id;
  const fileCategories = tenderNavData?.filesCats || [];
  const previousGeneratedFiles = useMemo(
    () => biddingData?.previousGeneratedFiles || [],
    [biddingData?.previousGeneratedFiles]
  );
  const procurementId = tenderNavData?.id;
  const procurementName = useMemo(() => {
    const nameBox = tenderNavData?.info.name;

    return nameBox && isFieldString(nameBox.firstField) ? nameBox.firstField.string : '';
  }, [tenderNavData?.info.name]);

  const [checkedFiles, setCheckedFiles] = useState(previousGeneratedFiles);

  const onCheck = useCallback(checkedKeys => {
    setCheckedFiles(checkedKeys);
  }, []);

  const onClose = useCallback(() => {
    onCloseModal();
    setCheckedFiles([]);
  }, [onCloseModal]);

  const [generateGroups, { loading: generateGroupsLoading }] = useGenerateBidGroups();
  const onGenerateTaskGroup = useCallback(() => {
    if (bidId) {
      trackBTGenerateTask(bidId);

      generateGroups({
        bidId,
        selectedFiles: checkedFiles,
        onComplete: onClose
      });
    }
  }, [bidId, checkedFiles, generateGroups, onClose]);

  const hitLimits = useMemo(() => {
    return hitGroupLimits(biddingData?.questionGroups || [], checkedFiles);
  }, [checkedFiles, biddingData]);

  const restCheckedFiles = useMemo(() => {
    if (!previousGeneratedFiles || !previousGeneratedFiles.length) {
      return checkedFiles;
    }

    return checkedFiles.filter(file => !previousGeneratedFiles.includes(file));
  }, [checkedFiles, previousGeneratedFiles]);

  return (
    <Modal
      title={t('BiddingTool.prePopulateModalTitle')}
      visible
      onCancel={onClose}
      onOk={onGenerateTaskGroup}
      closable
      footer={[
        <Button key="cancel" type={'text'} onClick={onClose}>
          {t('Common.cancel')}
        </Button>,
        <Button
          key="pre-populate"
          type={'primary'}
          onClick={onGenerateTaskGroup}
          disabled={generateGroupsLoading || !restCheckedFiles.length}
        >
          {t('BiddingTool.prePopulateButton')}
        </Button>
      ]}
      width="64rem"
    >
      <div className={styles.modalContent}>
        <p className={styles.description}>{t('BiddingTool.prePopulateModalDesc')}</p>
        <LearnMoreLink
          className={styles.learnMoreLink}
          title={t('BiddingTool.learnMoreAboutPrePopulatedTasks')}
          href={`${import.meta.env.VITE_HELPDOCS_DOMAIN}`}
        />
        {!!fileCategories.length && (
          <Form form={form} className={styles.form}>
            <div className={classNames(styles.fileTree)}>
              <TenderFilesTree
                fileCategories={fileCategories}
                checkedFiles={checkedFiles}
                precheckFiles={previousGeneratedFiles || []}
                restCheckedFiles={restCheckedFiles}
                onCheck={onCheck}
                hitLimits={hitLimits}
                procurementId={procurementId}
                procurementName={procurementName || undefined}
              />
            </div>
          </Form>
        )}
      </div>
    </Modal>
  );
};

export default PrePopulateModal;
